import { type JWT, fetchAuthSession } from 'aws-amplify/auth'
import type { TFunction } from 'i18next'
import { big_size, min_size, thumbnail_size } from './config'

export const tee = <T>(a: T): T => (console.log(a), a)

// これでちゃんと型が推論されるのでanyを許可
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const omit = <T extends { [key: string]: any }, Ks extends (keyof T)[]>(obj: T, ...keys: Ks): Omit<T, Ks[number]> => {
	const ret = { ...obj }
	for (const k of keys) if (k in ret) delete ret[k]
	return ret
}

export const pinchDistance = (touch0: React.Touch, touch1: React.Touch): number =>
  Math.sqrt((touch0.clientX - touch1.clientX) ** 2 + (touch0.clientY - touch1.clientY) ** 2)

const sizeImgURL = (size: string, url: string): string => {
	// todo バックエンドのURLに合わせて変更
	const urls_dirs = url.split('/')
	const base_path_dirs = urls_dirs.slice(0, urls_dirs.length - 2)
	const file_name = urls_dirs.at(-1) as string
	const webp_name = file_name.replace(/\.(png|jpg|jpeg)/, '.avif')
	return [...base_path_dirs, size, webp_name].join('/')
}
export const minURL = sizeImgURL.bind(null, 'min')
export const bigURL = sizeImgURL.bind(null, 'big')
export const thumbnailURL = sizeImgURL.bind(null, 'thumbnail')
export const mkUrls = (url: string) => ({
  original: url,
  medium: minURL(url),
  large: bigURL(url),
  thumbnail: thumbnailURL(url),
})

const reSize = (size: number, photo: {width: number, height: number}): {width: number, height: number} => {
	const ratio = photo.width / photo.height
	return photo.width > photo.height ? { width: size, height: Math.round(size / ratio) } : { width: Math.round(size * ratio), height: size }
}
// todo size設定によって変更
export const bigSize = reSize.bind(null, big_size)
export const minSize = reSize.bind(null, min_size)
export const thumbnailSize = reSize.bind(null, thumbnail_size)

// 途中で切り替わることもあるので関数とする
export const isTouchDevice = (): boolean => 'ontouchstart' in window || navigator.maxTouchPoints > 0

export const isMobile = () => /iPhone|Android.+Mobile/.test(navigator.userAgent)

export const isTablet = () => /iPad|Android/.test(navigator.userAgent) && !isMobile()

export const isClosedInDays = (old_time: number | undefined, now: number, day: number) =>
  Boolean(old_time && now - old_time < day * 24 * 60 * 60 * 1000)

// 幅が狭いかどうか
export const isWideWidth = (): boolean => window.innerWidth > 640

// ローテートされてるので最新を使ったほうが多分良い
export const getJWT = (): Promise<JWT | undefined> =>
  fetchAuthSession().then(s=> s.tokens?.accessToken)

export const byte2str = (bytes: number): string =>
  bytes > 1024 ** 3 ? (bytes / 1024 ** 3).toFixed(1) + 'GB' :
  bytes > 1024 ** 2 ? (bytes / 1024 ** 2).toFixed(1) + 'MB' :
                      (bytes / 1024).toFixed(1) + 'KB'

export const userEnv = () => ({
	user_agent: navigator.userAgent,
	language: navigator.language,
	isMobile: isMobile(),
	isTablet: isTablet(),
	isTouchDevice: isTouchDevice(),
})

export const getStaticColumsLength = (width: number) => Math.floor(width / 180)

export const timeAgo = (t: TFunction, date: Date): string => {
  const diff = new Date().getTime() - date.getTime()
  const diff_sec = Math.floor(diff / 1000)
  if (diff_sec < 60) return t('time.just_now')
  const diff_min = Math.floor(diff_sec / 60)
  if (diff_min === 1) return t('time.minute_ago')
  if (diff_min < 60) return t('time.minutes_ago', {num: diff_min})
  const diff_hour = Math.floor(diff_min / 60)
  if (diff_hour === 1) return t('time.hour_ago')
  if (diff_hour < 24) return t('time.hours_ago', {num: diff_hour})
  const diff_day = Math.floor(diff_hour / 24)
  if (diff_day === 1) return t('time.yesterday')
  if (diff_day < 30) return t('time.days_ago', {num: diff_day})
  const diff_month = Math.floor(diff_day / 30)
  if (diff_month === 1) return t('time.month_ago')
  if (diff_month < 12) return t('time.months_ago', {num: diff_month})
  const diff_year = Math.floor(diff_day / 365)
  if (diff_year === 1) return t('time.year_ago')
  return t('time.years_ago', {num: diff_year})
}

const text_encoder = new TextEncoder()
const segmenter = new Intl.Segmenter(undefined, {granularity: 'grapheme'})

export const graphemeLength = (text: string): number => [...segmenter.segment(text)].length

// 検証（textLengthErrorMsg）と切り詰めで数え方がズレると、
// カウンタの表示と入力できる量が食い違うので同じsegmenterを共有する。
export const truncateByGrapheme = (text: string, max_length: number): string => {
  const graphemes = [...segmenter.segment(text)]
  return graphemes.length <= max_length ? text : graphemes.slice(0, max_length).map(g => g.segment).join('')
}

export const textLengthErrorMsg = (
  t: TFunction, text: string, min_length: number, max_length: number, max_bytes: number
): string => {
  const name_length = graphemeLength(text)
  const name_bytes = text_encoder.encode(text).length
  return name_length < min_length ? t('error.text_length_fewer', {limit: min_length, num: name_length}) :
         name_length > max_length ? t('error.text_length_over', {limit: max_length, num: name_length}) :
         name_bytes > max_bytes   ? t('error.text_bytes_over', {limit: max_bytes, num: name_bytes}) :
                                    ''
}

export const isStorageDisplayAsPhotoCount = (storage_count_files: number): boolean => storage_count_files < 10000

// ストレージ表示タイプに基づいて適切なダウンロードURLを返す
export const getDownloadUrl = (photo: {urls: {original: string, large: string}}, storage_count_files: number): string =>
  isStorageDisplayAsPhotoCount(storage_count_files) ? photo.urls.large : photo.urls.original

// DMをlast_appendとlast_read_atの新しい方でソート（新しい順）
export const sortDMsByLatestActivity = <T extends {last_append?: Date | null, last_read_at?: Date | null}>(dms: T[]): T[] => {
  return [...dms].sort((a, b) => {
    const a_time = Math.max(
      a.last_append?.getTime() || 0,
      a.last_read_at?.getTime() || 0
    )
    const b_time = Math.max(
      b.last_append?.getTime() || 0,
      b.last_read_at?.getTime() || 0
    )
    return b_time - a_time // 降順（新しい順）
  })
}
